/**
 * IBAN helpers for the manual payout bank-details form.
 *
 * The backend is the source of truth (it normalises and validates with a full
 * ISO 7064 mod-97 checksum), but we validate client-side too for instant feedback.
 */

/** Uppercase and strip all whitespace, matching how the backend normalises an IBAN. */
export function normalizeIban(input: string): string {
    return input.replace(/\s+/g, '').toUpperCase();
}

/** Group an IBAN into blocks of 4 for display, e.g. "NL91 ABNA 0417 1643 00". */
export function formatIban(iban: string): string {
    return normalizeIban(iban).replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Validate an IBAN structurally and via the ISO 7064 mod-97 checksum.
 * Accepts spaces and any case.
 */
export function isValidIban(input: string): boolean {
    const iban = normalizeIban(input);

    // Country code (2 letters) + 2 check digits + up to 30 alphanumerics.
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{1,30}$/.test(iban)) {
        return false;
    }

    // Move the first four characters to the end.
    const rearranged = iban.slice(4) + iban.slice(0, 4);

    // Replace each letter with two digits: A = 10, B = 11, ... Z = 35.
    const expanded = rearranged.replace(/[A-Z]/g, (ch) => (ch.charCodeAt(0) - 55).toString());

    // Compute mod 97 piecewise to avoid overflowing Number on long IBANs.
    let remainder = 0;
    for (let i = 0; i < expanded.length; i++) {
        remainder = (remainder * 10 + (expanded.charCodeAt(i) - 48)) % 97;
    }

    return remainder === 1;
}

/** Mask all but the first and last four characters, e.g. "NL91 •••• •••• •••• 4300". */
export function maskIban(input: string): string {
    const iban = normalizeIban(input);
    if (iban.length <= 8) {
        return formatIban(iban);
    }
    const masked = iban.slice(0, 4) + '•'.repeat(iban.length - 8) + iban.slice(-4);
    return masked.replace(/(.{4})/g, '$1 ').trim();
}
